import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { CheckCircle, Clock, WarningCircle, House } from '@phosphor-icons/react';
import { NightScheduleWarning } from './NightScheduleWarning';
import { trackEvent } from '../lib/metaPixel';

const pad = (n) => String(n).padStart(2, '0');

/**
 * Full-screen confirmation shown after the customer uploads their receipt.
 * Displays a countdown for the expected delivery window:
 *  - TikTok Likes: 60 min (may take up to 24 hs on busy days)
 *  - Rest of services: 30 min (may take up to 6 hs on busy days)
 */
export const OrderCompletedScreen = ({ order, onClose }) => {
  const isTiktokLikes = useMemo(() => {
    if (!order) return false;
    return (
      order.platform === 'TikTok' &&
      (order.service_type === 'likes' || /like/i.test(order.service_name || ''))
    );
  }, [order]);

  const totalSeconds = isTiktokLikes ? 60 * 60 : 30 * 60;
  const delayLabel = isTiktokLikes ? '24 hs' : '6 hs';
  const minutesLabel = isTiktokLikes ? '60 min' : '30 min';

  const [remaining, setRemaining] = useState(totalSeconds);

  // 🎯 Meta Pixel: Purchase event — se dispara UNA sola vez cuando esta pantalla
  // se monta (equivale a "comprobante enviado y pedido en cola").
  useEffect(() => {
    if (!order?.id) return;
    // key idempotente en sessionStorage por order.id: si el usuario vuelve a esta
    // pantalla no re-fireamos el evento y no infla las conversiones.
    try {
      const key = `fbq_purchase_${order.id}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* if storage blocked, still fire once per mount — acceptable */
    }
    const value = Number(
      order.total_price_ars ??
        order.total_ars ??
        order.price_ars ??
        order.amount ??
        0
    );
    trackEvent('Purchase', {
      content_name: order.service_name || 'Order',
      content_category: order.platform || undefined,
      content_ids: order.service_id ? [order.service_id] : undefined,
      contents: order.service_id
        ? [{ id: order.service_id, quantity: 1, item_price: value }]
        : undefined,
      value,
      currency: 'ARS',
      order_id: order.id,
      // event_id compartido con el evento server-side (CAPI) para que Meta deduplique
      eventID: `purchase_${order.id}`,
    });
  }, [order?.id, order?.service_id, order?.service_name, order?.platform, order?.total_price_ars, order?.total_ars, order?.price_ars, order?.amount]);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const progress = Math.min(100, ((totalSeconds - remaining) / totalSeconds) * 100);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-background via-background to-[#00E676]/5"
      data-testid="order-completed-screen"
    >
      <div className="w-full max-w-2xl">
        {/* Hero check */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-[#00E676]/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-[#00E676] to-emerald-600 flex items-center justify-center shadow-2xl shadow-[#00E676]/40">
              <CheckCircle size={72} weight="fill" className="text-white" />
            </div>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 tracking-tight"
            data-testid="order-completed-title"
          >
            ¡Pedido realizado!
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-md">
            Tu comprobante fue recibido y tu pedido ya está en cola.
            {order?.id && (
              <>
                <br />
                <span className="text-sm">
                  Código:{' '}
                  <span className="font-mono font-semibold">
                    #{String(order.id).slice(0, 8)}
                  </span>
                </span>
              </>
            )}
          </p>
        </div>

        {/* Countdown card */}
        <div
          className="relative overflow-hidden rounded-2xl border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 via-background to-[#0066FF]/10 p-8 sm:p-10 mb-6"
          data-testid="delivery-countdown-card"
        >
          <div className="flex items-center gap-2 mb-3 justify-center">
            <Clock size={22} weight="duotone" className="text-[#00E676]" />
            <span className="text-xs font-bold tracking-widest text-[#00E676] uppercase">
              Tiempo estimado de entrega
            </span>
          </div>

          <div
            className="flex items-center justify-center gap-2 sm:gap-4 mb-6"
            data-testid="countdown-timer"
          >
            <div className="flex flex-col items-center bg-background/80 backdrop-blur rounded-2xl px-6 sm:px-10 py-4 sm:py-6 border border-border min-w-[110px] sm:min-w-[140px]">
              <div className="text-5xl sm:text-7xl font-bold font-mono tabular-nums leading-none text-[#00E676]">
                {pad(mm)}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground mt-2">
                minutos
              </div>
            </div>
            <div className="text-5xl sm:text-7xl font-bold text-[#00E676] leading-none -mt-6">:</div>
            <div className="flex flex-col items-center bg-background/80 backdrop-blur rounded-2xl px-6 sm:px-10 py-4 sm:py-6 border border-border min-w-[110px] sm:min-w-[140px]">
              <div className="text-5xl sm:text-7xl font-bold font-mono tabular-nums leading-none text-[#00E676]">
                {pad(ss)}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground mt-2">
                segundos
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-background/60 rounded-full h-2 mb-4 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00E676] to-emerald-500 transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-center text-sm sm:text-base text-foreground">
            Tu pedido se entrega habitualmente en hasta{' '}
            <strong className="text-[#00E676]">{minutesLabel}</strong>.
          </p>
        </div>

        {/* Delay notice */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 mb-4">
          <WarningCircle size={22} weight="fill" className="text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            {isTiktokLikes ? (
              <>
                <strong>Demoras posibles:</strong> en caso de demora el pedido puede tardar hasta{' '}
                <strong>{delayLabel}</strong> debido al sistema de la aplicación de TikTok.
              </>
            ) : (
              <>
                <strong>Demoras posibles:</strong> en caso de demoras puede tardar hasta{' '}
                <strong>{delayLabel}</strong>.
              </>
            )}
          </div>
        </div>

        {/* Horario nocturno (00:00 - 08:00 AR) */}
        <div className="mb-4">
          <NightScheduleWarning />
        </div>

        {/* Public profile notice */}
        <div
          className="flex items-start gap-3 p-4 rounded-xl bg-[#0066FF]/10 border border-[#0066FF]/30 mb-6"
          data-testid="public-profile-reminder"
        >
          <span className="text-[#0066FF] text-xl leading-none mt-0.5">🔓</span>
          <div className="text-sm">
            <strong>Mantené tu perfil público:</strong> tu perfil debe estar abierto hasta que se
            complete el pedido. Una vez entregado, podés volver a ponerlo en privado.
          </div>
        </div>

        {/* Order details */}
        {order && (
          <div className="rounded-xl border border-border bg-muted/30 p-5 mb-6 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Servicio</span>
              <span className="font-medium text-right">{order.service_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cantidad a entregar</span>
              <span className="font-medium">
                {(order.delivered_quantity || order.quantity || 0).toLocaleString('es-AR')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">URL destino</span>
              <span className="font-mono text-xs truncate ml-2 max-w-[260px]" title={order.target_url}>
                {order.target_url}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-border">
              <span className="text-muted-foreground">Total pagado</span>
              <span className="font-bold text-primary">
                ${(order.price || 0).toLocaleString('es-AR')}
              </span>
            </div>
          </div>
        )}

        <Button
          onClick={onClose}
          variant="outline"
          className="w-full"
          size="lg"
          data-testid="order-completed-home-button"
        >
          <House size={18} className="mr-2" />
          Volver al inicio
        </Button>
      </div>
    </div>
  );
};
